import { useEffect } from 'react';

// Même logique que dans api.js : en production tout est servi par le même
// serveur, donc les chemins sont relatifs. Sans cela, l'adresse « localhost »
// du fichier .env serait figée dans le build et les documents deviendraient
// inaccessibles pour les autres utilisateurs.
const API_URL =
  process.env.NODE_ENV === 'production'
    ? '/api'
    : process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const FILES_BASE = API_URL.replace(/\/api\/?$/, '');

export function fileUrl(storedName) {
  return `${FILES_BASE}/uploads/${storedName}`;
}

export function formatFileSize(bytes) {
  if (bytes == null) return '';
  if (bytes < 1024) return `${bytes} o`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} Ko`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} Mo`;
}

export function projectCode(id) {
  return `ZGP-${String(id).padStart(3, '0')}`;
}

export function taskCode(id) {
  return `TSK-${String(id).padStart(4, '0')}`;
}

export function initials(name) {
  if (!name) return '?';
  return name.substring(0, 2).toUpperCase();
}

/**
 * Nom affiché d'une personne : son nom complet s'il est renseigné (page Accès),
 * sinon l'identifiant remis en forme (« mohamed_saadi » → « Mohamed Saadi »).
 * Les identifiants techniques ne doivent plus apparaître dans les documents.
 */
export function userDisplayName(user) {
  if (!user) return 'Non désigné';
  if (user.full_name && user.full_name.trim()) return user.full_name.trim();
  return nomDepuisIdentifiant(user.username);
}

export function nomDepuisIdentifiant(username) {
  if (!username) return 'Non désigné';
  return username
    .split(/[._-]+/)
    .filter(Boolean)
    .map((m) => m.charAt(0).toUpperCase() + m.slice(1))
    .join(' ');
}

/** Initiales d'une personne pour les avatars : « Mohamed Saadi » → « MS ». */
export function userInitials(user) {
  const nom = userDisplayName(user);
  const mots = nom.split(' ').filter(Boolean);
  if (mots.length >= 2) return (mots[0][0] + mots[mots.length - 1][0]).toUpperCase();
  return nom.substring(0, 2).toUpperCase();
}

/**
 * Ferme une fenêtre modale avec la touche Échap. À appeler dans chaque modale
 * avec sa fonction de fermeture.
 */
export function useFermerAvecEchap(onClose) {
  useEffect(() => {
    if (!onClose) return undefined;
    const handler = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);
}

/** Montant renseigné ou non : évite d'afficher « 0 MAD » pour une absence de donnée. */
export function montantOuAbsent(valeur, absent = 'Non renseigné') {
  const n = Number(valeur);
  if (!valeur || !n) return absent;
  return formatMontant(n);
}

export function formatDate(dateStr) {
  if (!dateStr) return '-';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = d.getFullYear();
  return `${day}/${month}/${year}`;
}

// Les <input type="date"> natifs affichent mm/dd/yyyy ou jj/mm/aaaa selon la
// langue configurée sur la machine de l'utilisateur (pas selon la langue de
// la page) — ces deux fonctions permettent d'utiliser un champ texte qui
// affiche toujours jj/mm/aaaa, quel que soit le PC.
export function isoToDisplayDate(iso) {
  if (!iso) return '';
  const m = String(iso).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return '';
  return `${m[3]}/${m[2]}/${m[1]}`;
}

export function displayDateToIso(display) {
  if (!display) return '';
  const m = String(display).trim().match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (!m) return null; // saisie incomplète ou invalide
  const day = parseInt(m[1], 10);
  const month = parseInt(m[2], 10);
  const year = parseInt(m[3], 10);
  // On vérifie que la date existe réellement : sans ce contrôle, une saisie
  // comme 15/15/2026 produisait "2026-15-15" — une date impossible qui
  // cassait ensuite tout le calcul d'échelle du diagramme de Gantt.
  if (month < 1 || month > 12) return null;
  const daysInMonth = new Date(year, month, 0).getDate();
  if (day < 1 || day > daysInMonth) return null;
  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

// Renvoie un objet Date valide, ou null si la valeur est absente ou corrompue.
// Permet aux vues de survivre à une donnée invalide au lieu de planter.
export function toValidDate(value) {
  if (!value) return null;
  const d = new Date(value);
  return isNaN(d.getTime()) ? null : d;
}

export function timeAgo(dateStr) {
  if (!dateStr) return '';
  const diffMs = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return "à l'instant";
  if (mins < 60) return `il y a ${mins} min`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `il y a ${hours} h`;
  const days = Math.floor(hours / 24);
  return `il y a ${days} j`;
}

export const STATUS_LABELS = {
  Todo: 'À faire',
  In_Progress: 'En cours',
  In_Review: 'Revue',
  Done: 'Terminé',
  Blocked: 'Bloqué',
};

export const STATUS_BADGE_CLASS = {
  Todo: 'badge-todo',
  In_Progress: 'badge-active',
  In_Review: 'badge-review',
  Done: 'badge-completed',
  Blocked: 'badge-blocked',
};

// Priorité d'une tâche. Les valeurs restent en anglais dans la base (elles
// servent de clés), mais rien ne doit s'afficher en anglais à l'écran.
export const TASK_PRIORITIES = ['Urgent', 'High', 'Medium', 'Low'];

export const PRIORITY_LABELS = {
  Urgent: 'Urgente',
  High: 'Haute',
  Medium: 'Moyenne',
  Low: 'Basse',
};

export const PRIORITY_BADGE_CLASS = {
  Urgent: 'badge-priority-high',
  High: 'badge-priority-high',
  Medium: 'badge-priority-medium',
  Low: 'badge-priority-low',
};

export const PROJECT_STATUS_BADGE_CLASS = {
  Active: 'badge-active',
  Delayed: 'badge-delayed',
  Completed: 'badge-completed',
  Cancelled: 'badge-blocked',
};

export const PROJECT_STATUS_LABELS = {
  Active: 'Actif',
  Delayed: 'En retard',
  Completed: 'Terminé',
  Cancelled: 'Annulé',
};

// Rôles autorisés à annuler un projet
export const CAN_CANCEL_PROJECT_ROLES = ['Director', 'Project_Lead'];

// Rôles autorisés à réinitialiser le mot de passe d'un autre utilisateur.
// Doit rester aligné avec ROLES_REINITIALISATION dans backend/server_mock.js :
// le contrôle côté serveur fait foi, celui-ci ne fait que masquer le bouton.
export const CAN_RESET_PASSWORD_ROLES = ['Director', 'Project_Lead'];

export const PROJECT_PRIORITIES = ['P1', 'P2', 'P3', 'P4'];

export const PROJECT_PRIORITY_LABELS = {
  P1: 'P1 - Critique',
  P2: 'P2 - Haute',
  P3: 'P3 - Moyenne',
  P4: 'P4 - Basse',
};

export const PROJECT_PRIORITY_BADGE_CLASS = {
  P1: 'badge-priority-high',
  P2: 'badge-priority-high',
  P3: 'badge-priority-medium',
  P4: 'badge-priority-low',
};

// --- Demandes d'achat --------------------------------------------------------
// Les numéros de requête (ZMSM-R-…) étaient auparavant saisis dans la
// description des tâches ; ils ont désormais un champ dédié, ce qui les rend
// filtrables.

export const PURCHASE_STATUSES = ['a_lancer', 'lancee', 'commandee', 'livree'];

export const PURCHASE_STATUS_LABELS = {
  a_lancer: 'À lancer',
  lancee: 'Requête lancée',
  commandee: 'Commandée',
  livree: 'Livrée',
};

export const PURCHASE_STATUS_BADGE = {
  a_lancer: 'badge-todo',
  lancee: 'badge-active',
  commandee: 'badge-review',
  livree: 'badge-completed',
};

// --- Échéances ---------------------------------------------------------------

/**
 * Avancement affiché d'une tâche : une tâche terminée vaut 100 %, quelle que
 * soit sa checklist. Le serveur applique déjà la même règle ; ce garde-fou
 * couvre les données arrivées par un autre chemin.
 */
export function avancementTache(task) {
  if (!task) return 0;
  if (task.status === 'Done') return 100;
  return task.progress_percent || 0;
}

/** Une tâche est en retard si son échéance est passée et qu'elle n'est pas terminée. */
export function isOverdue(task) {
  if (!task || !task.due_date || task.status === 'Done') return false;
  return String(task.due_date).slice(0, 10) < new Date().toISOString().slice(0, 10);
}

/** Nombre de jours de retard (0 si la tâche n'est pas en retard). */
export function joursDeRetard(task) {
  if (!isOverdue(task)) return 0;
  const echeance = new Date(String(task.due_date).slice(0, 10));
  const jour = new Date(new Date().toISOString().slice(0, 10));
  return Math.round((jour - echeance) / 86400000);
}

// Rôles autorisés à archiver / restaurer un projet.
// Doit rester aligné avec ROLES_ARCHIVAGE dans backend/server_mock.js.
export const CAN_ARCHIVE_PROJECT_ROLES = ['Director', 'Project_Lead'];

// --- Montants ----------------------------------------------------------------
// Les budgets étaient toujours affichés en millions : sous 100 000 MAD, tout
// devenait « 0.0 M MAD ». On adapte l'unité au montant.

export function formatMontant(valeur) {
  const n = Number(valeur) || 0;
  const signe = n < 0 ? '-' : '';
  const abs = Math.abs(n);
  if (abs >= 1000000) return `${signe}${(abs / 1000000).toFixed(2)} M MAD`;
  if (abs >= 1000) return `${signe}${(abs / 1000).toFixed(0)} kMAD`;
  return `${signe}${abs.toFixed(0)} MAD`;
}

/** Version courte pour les tuiles et les tableaux serrés. */
export function formatMontantCourt(valeur) {
  const n = Number(valeur) || 0;
  const signe = n < 0 ? '-' : '';
  const abs = Math.abs(n);
  if (abs >= 1000000) return `${signe}${(abs / 1000000).toFixed(1)} M`;
  if (abs >= 1000) return `${signe}${(abs / 1000).toFixed(0)} k`;
  return `${signe}${abs.toFixed(0)}`;
}

// --- Dépenses de projet ------------------------------------------------------
// Tout ce qui ne passe pas par une demande d'achat : sorties magasin,
// interventions de maintenance, prestations externes.

export const EXPENSE_CATEGORIES = ['sortie_magasin', 'maintenance', 'prestation', 'achat', 'autre'];

export const EXPENSE_CATEGORY_LABELS = {
  sortie_magasin: 'Sortie magasin',
  maintenance: 'Maintenance',
  prestation: 'Prestation externe',
  achat: 'Achat direct',
  autre: 'Autre',
};

export const EXPENSE_STATUSES = ['engage', 'depense'];

export const EXPENSE_STATUS_LABELS = {
  engage: 'Engagé',
  depense: 'Dépensé',
};

export const EXPENSE_STATUS_BADGE = {
  engage: 'badge-review',
  depense: 'badge-completed',
};

// Rôles autorisés à supprimer la dépense saisie par quelqu'un d'autre.
// Doit rester aligné avec ROLES_SUPPRESSION_DEPENSE dans backend/server_mock.js.
export const CAN_DELETE_ANY_EXPENSE_ROLES = ['Director', 'Project_Lead'];

// --- Rôles -------------------------------------------------------------------
// Les rôles restent en anglais dans la base (ils servent de clés aux contrôles
// d'accès), mais ne doivent jamais s'afficher tels quels.

export const ROLES = ['Director', 'Project_Lead', 'Manager', 'Supervisor', 'Engineer'];

export const ROLE_LABELS = {
  Director: 'Directeur',
  Project_Lead: 'Chef de projet',
  Manager: 'Manager',
  Supervisor: 'Superviseur',
  Engineer: 'Ingénieur',
};

// Rôles autorisés à importer des tâches en lot.
// Doit rester aligné avec ROLES_IMPORT dans backend/server_mock.js.
export const CAN_IMPORT_TASKS_ROLES = ['Director', 'Project_Lead', 'Manager'];

// --- Organisation, rotations et intérim --------------------------------------
// Doit rester aligné avec ROLES_GESTION_ABSENCES dans backend/server_mock.js.
export const CAN_MANAGE_ABSENCES_ROLES = ['Director', 'Project_Lead'];

// Seule la direction modifie les rôles et les droits de signature.
export const CAN_EDIT_ORGANISATION_ROLES = ['Director'];

/**
 * Nom à afficher pour le responsable d'une tâche, intérim compris.
 * Pendant une rotation, c'est l'intérimaire qui traite : le titulaire reste
 * affiché, mais on précise qui assure le relais.
 */
export function responsableAffiche(task, users) {
  const titulaire = userDisplayName(users.find((u) => u.id === task?.assigned_to));
  if (!task?.interim_name) return titulaire;
  return `${titulaire} (intérim : ${task.interim_name})`;
}

// Compte autorisé à lancer la maintenance du portefeuille — purge de l'archive
// et renumérotation. Plus restreint que le rôle Directeur, car ces opérations
// sont irréversibles et concernent toute l'entreprise.
// Doit rester aligné avec COMPTE_MAINTENANCE dans backend/server_mock.js.
export const COMPTE_MAINTENANCE = 'admin';

export function peutFaireMaintenance(user) {
  return (
    !!user &&
    String(user.username).toLowerCase() === COMPTE_MAINTENANCE &&
    user.role === 'Director'
  );
}

// --- Blocages ----------------------------------------------------------------
// Une tâche bloquée doit dire ce qu'on attend et de qui. Les seuils de remontée
// sont les mêmes qu'au serveur : 5 jours vers le chef de service, 8 vers la
// direction.

export const BLOCAGE_ORIGINES = ['personne', 'service', 'externe'];

export const BLOCAGE_ORIGINE_LABELS = {
  personne: 'Une personne',
  service: 'Un service',
  externe: 'Un intervenant extérieur',
};

export const ESCALADE_SERVICE_JOURS = 5;
export const ESCALADE_DIRECTION_JOURS = 8;

/** Nombre de jours entiers depuis la déclaration du blocage. */
export function joursDeBlocage(tache) {
  if (!tache || tache.status !== 'Blocked' || !tache.blocage_depuis) return 0;
  const debut = new Date(String(tache.blocage_depuis).slice(0, 10));
  const ecart = Math.floor((new Date(new Date().toISOString().slice(0, 10)) - debut) / 86400000);
  return ecart > 0 ? ecart : 0;
}

/** « Bloqué depuis 6 jours — attend Rados ». */
export function libelleBlocage(tache) {
  if (!tache || tache.status !== 'Blocked') return '';
  if (tache.blocage_libelle) return tache.blocage_libelle;
  const j = joursDeBlocage(tache);
  const duree = j === 0 ? "depuis aujourd'hui" : `depuis ${j} jour${j > 1 ? 's' : ''}`;
  const qui = String(tache.blocage_qui || '').trim();
  return `Bloqué ${duree}${qui ? ` — attend ${qui}` : ''}`;
}

/** null | 'service' | 'direction' : jusqu'où le blocage est remonté. */
export function niveauEscalade(jours) {
  if (jours >= ESCALADE_DIRECTION_JOURS) return 'direction';
  if (jours >= ESCALADE_SERVICE_JOURS) return 'service';
  return null;
}

// --- Classification des projets ----------------------------------------------
// Deux axes indépendants, demandés pour trier le portefeuille :
//   le TYPE   — à quoi sert le projet (améliorer, construire, augmenter) ;
//   le SERVICE — quelle discipline le pilote.
//
// Les clés sont sans accent : elles partent en base et se retrouvent dans les
// URL de filtre. Les libellés accentués restent côté affichage.

export const PROJECT_TYPES = ['Amelioration', 'Infrastructure', 'Capacite'];

export const PROJECT_TYPE_LABELS = {
  Amelioration: 'Amélioration',
  Infrastructure: 'Infrastructure',
  Capacite: 'Augmentation de capacité',
};

export const PROJECT_TYPE_BADGE_CLASS = {
  Amelioration: 'badge-type-amelioration',
  Infrastructure: 'badge-type-infrastructure',
  Capacite: 'badge-type-capacite',
};

export const PROJECT_SERVICES = ['Metallurgie', 'EIA', 'Mecanique', 'Maintenance'];

export const PROJECT_SERVICE_LABELS = {
  Metallurgie: 'Métallurgie',
  EIA: 'EIA',
  Mecanique: 'Mécanique',
  Maintenance: 'Maintenance',
};

/** Libellé d'un type, avec repli sur la valeur brute pour les anciens projets. */
export function libelleType(valeur) {
  if (!valeur) return '';
  return PROJECT_TYPE_LABELS[valeur] || valeur;
}

export function libelleService(valeur) {
  if (!valeur) return '';
  return PROJECT_SERVICE_LABELS[valeur] || valeur;
}
